import { useRef, useState } from 'react';
import { Link } from 'react-router';
import { Reveal } from '@components/motion';
import { featuredInProgram } from '@/data/projects';
import type { DocCollection } from '@/data/resources';
import {
    focusArticles,
    focusDocuments,
    focusGallery,
    focusStories,
    type FocusContent,
    type FocusObjective,
} from '@/data/focusContent';
import { ArticleCard, DownloadGrid, GalleryGrid, SectionLabel } from '@components/pages/ResourceSections';
import { Lightbox, type LightboxContent } from '@components/pages/ResourceActions';
import { Img } from '@/components/Img';

const HERO_BG = '/focus/hero-bg.webp';
const JUSTICE_ILLUSTRATION = '/focus/justice-illustration.webp';
const PROJECT_IMAGES = ['/landing/stories/story1.webp', '/landing/stories/story2.webp', '/landing/stories/story3.webp'];
const OBJECTIVE_SHOT = '/landing/innovations/contract-card.webp';

const PROJECTS_INTRO =
    'Behind every case resolved is a person who finally found a way forward. These are the projects doing that work — some built in-house, some run with partners, all of them measured on whether a real case moved.';

/* ── Hero ─────────────────────────────────────────────────────────────── */

export function FocusHero({
    titleLead,
    titleBold,
    subtitle,
    intro,
    heroImage,
}: {
    titleLead: string;
    titleBold: string;
    subtitle?: string;
    intro: string;
    /** Per-area background from the CMS; the shared one otherwise. */
    heroImage?: string;
}) {
    return (
        <section className="relative flex min-h-[620px] items-center overflow-hidden bg-[#ededed] lg:min-h-[88dvh]">
            <Img src={heroImage || HERO_BG} alt="" priority className="absolute inset-0 size-full object-cover" />

            {/* Low-poly Lady Justice, standing on the base of the frame */}
            <Img
                src={JUSTICE_ILLUSTRATION}
                alt=""
                className="pointer-events-none absolute bottom-0 right-[2%] h-[58%] w-auto max-w-none object-contain object-bottom opacity-50 sm:opacity-75 lg:right-[5%] lg:h-[86%] lg:opacity-100"
            />

            <div className="relative z-10 mx-auto w-full max-w-[1640px] px-5 pb-16 pt-[140px] lg:px-[140px] lg:pb-24 lg:pt-[190px]">
                {/* The right third of the frame belongs to the illustration, so the copy
                    is capped short of it. Long titles (e.g. "Accountability") push the
                    intro onto its own line rather than squeezing it. */}
                <div className="flex flex-col gap-7 lg:flex-row lg:flex-wrap lg:items-end lg:gap-x-12 lg:gap-y-8">
                    <h1 className="font-primary text-[54px] font-bold leading-[0.98] tracking-[-2px] sm:text-[76px] lg:shrink-0 lg:text-[96px] lg:tracking-[-4px] xl:text-[118px]">
                        {titleLead && <span className="block font-light text-[#a0a3a5]">{titleLead}</span>}
                        <span className="block whitespace-nowrap text-[#08080c]">
                            {titleBold}
                            <span className="ml-[0.12em] inline-block size-[0.16em] rounded-full bg-[#f60] align-baseline" />
                        </span>
                    </h1>

                    <div className="min-w-0 lg:mb-[0.9em] lg:max-w-[380px] lg:basis-[300px] lg:grow">
                        {subtitle && <p className="mb-2 text-[14px] font-medium text-[#5e5e5e]">{subtitle}</p>}
                        <p className="max-w-[430px] text-[14px] leading-[1.75] text-[#333] lg:text-[15px]">{intro}</p>
                    </div>
                </div>

                <a
                    href="#projects"
                    className="mt-10 inline-flex items-center gap-3 rounded-full bg-[#08080c] py-3 pl-7 pr-3 text-[15px] font-semibold text-white transition-transform duration-300 hover:scale-[1.03] lg:mt-14"
                >
                    View Impact
                    <span className="flex size-7 items-center justify-center rounded-full bg-white/15">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                            <path
                                d="M5 12h13M12 5l7 7-7 7"
                                stroke="currentColor"
                                strokeWidth="2.4"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </span>
                </a>
            </div>
        </section>
    );
}

/* ── Highlighted projects ─────────────────────────────────────────────── */

function CardArrow({ label }: { label: string }) {
    return (
        <span
            aria-label={label}
            className="mt-1 flex size-9 shrink-0 items-center justify-center rounded-full border border-[#cfcfcf] text-[#08080c]"
        >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                    d="M5 12h13M12 5l7 7-7 7"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </span>
    );
}

export function HighlightedProjects({ program }: { program: string }) {
    const trackRef = useRef<HTMLDivElement>(null);
    const scrollBy = (dir: number) =>
        trackRef.current?.scrollBy({ left: dir * (trackRef.current.clientWidth * 0.55), behavior: 'smooth' });

    const projects = featuredInProgram(program);
    if (projects.length === 0) return null;

    return (
        <section id="projects" className="relative overflow-hidden bg-[#f4f4f4] py-16 lg:py-24">
            {/* Faint ring behind the card rail */}
            <span className="pointer-events-none absolute left-1/2 top-1/2 hidden size-[720px] -translate-x-[20%] -translate-y-1/2 rounded-full border border-black/[0.045] lg:block" />

            <div className="relative mx-auto max-w-[1640px] px-5 lg:px-[140px]">
                <div className="flex flex-col gap-10 lg:flex-row lg:gap-16">
                    <div className="lg:w-[330px] lg:shrink-0">
                        <h2 className="font-primary text-[38px] font-bold leading-[1.1] tracking-[-1.2px] lg:text-[46px]">
                            <span className="text-[#b3b3b3]">The Highlighted</span>
                            <br />
                            <span className="text-[#08080c]">Projects</span>
                        </h2>
                        <p className="mt-5 text-[13px] leading-[1.75] text-[#3d3d3d]">{PROJECTS_INTRO}</p>
                        <Link
                            to="/projects"
                            className="mt-7 inline-flex items-center justify-center rounded-[8px] bg-[#f60] px-6 py-3 text-[14px] font-bold text-white transition-transform duration-300 hover:scale-[1.03]"
                        >
                            View All Projects
                        </Link>
                    </div>

                    <div className="min-w-0 lg:flex-1">
                        <div
                            ref={trackRef}
                            className="scrollbar-hide flex snap-x snap-mandatory gap-6 overflow-x-auto scroll-smooth pb-2"
                        >
                            {projects.map((p, i) => (
                                <Link
                                    key={p.slug}
                                    to={`/projects/${p.slug}`}
                                    className="group w-[260px] shrink-0 snap-start lg:w-[286px]"
                                >
                                    <div className="h-[176px] w-full overflow-hidden rounded-[10px] bg-[#d9d9d9]">
                                        <Img
                                            src={p.image || PROJECT_IMAGES[i % PROJECT_IMAGES.length]}
                                            alt=""
                                            className="size-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
                                        />
                                    </div>
                                    <p className="mt-4 text-[17px] font-bold text-[#08080c]">{p.name}</p>
                                    <div className="mt-2 flex items-start justify-between gap-4">
                                        <p className="text-[12px] leading-[1.65] text-[#5e5e5e]">{p.tagline}</p>
                                        <CardArrow label={`Open ${p.name}`} />
                                    </div>
                                </Link>
                            ))}
                        </div>

                        {/* Carousel controls, centred under the rail */}
                        <div className="mt-8 flex items-center justify-center gap-6">
                            <button
                                type="button"
                                aria-label="Previous projects"
                                onClick={() => scrollBy(-1)}
                                className="flex size-8 items-center justify-center text-[#c4c4c4] transition-colors hover:text-[#08080c]"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                                    <path
                                        d="M15 6l-6 6 6 6"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </button>
                            <button
                                type="button"
                                aria-label="Next projects"
                                onClick={() => scrollBy(1)}
                                className="flex size-8 items-center justify-center text-[#08080c]"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                                    <path
                                        d="M9 6l6 6-6 6"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ── Take it for a spin ───────────────────────────────────────────────── */

/**
 * Heading left, standfirst right, then one full-width still with a translucent
 * play control centred on it. Opens the walkthrough in a lightbox when a video
 * URL is attached; without one the control is not rendered at all.
 */
export function TakeItForASpin({ video }: { video: FocusContent['video'] }) {
    const [open, setOpen] = useState<LightboxContent | null>(null);

    return (
        <section className="bg-white py-16 lg:py-24">
            <div className="mx-auto max-w-[1640px] px-5 lg:px-[140px]">
                <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:gap-24">
                    <h2 className="font-primary text-[32px] font-bold leading-[1.18] tracking-[-1.2px] text-[#08080c] lg:w-[560px] lg:shrink-0 lg:text-[44px] lg:tracking-[-1.6px]">
                        {video.heading}
                    </h2>
                    <p className="text-[13px] leading-[1.85] text-[#8a8a8a] lg:flex-1 lg:pt-2">{video.copy}</p>
                </div>

                <Reveal as="div" once variant="fade-up" className="mt-10 lg:mt-14">
                    <div className="relative h-[220px] w-full overflow-hidden rounded-[16px] bg-[#d9d9d9] lg:h-[300px]">
                        <Img src={video.thumbnail} alt="" className="size-full object-cover" />

                        {/* Always shown, as designed. With no URL attached yet the
                            lightbox opens on the still rather than an embed. */}
                        <button
                            type="button"
                            aria-label="Play the walkthrough"
                            onClick={() =>
                                setOpen({ title: video.heading, image: video.thumbnail, videoUrl: video.url })
                            }
                            className="group absolute left-1/2 top-1/2 flex size-[110px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-[#f60]/45 backdrop-blur-[1px] transition-transform duration-300 hover:scale-[1.06] lg:size-[124px]"
                        >
                            {/* Solid white triangle, nudged right so it reads as centred. */}
                            <svg
                                width="34"
                                height="34"
                                viewBox="0 0 24 24"
                                fill="white"
                                aria-hidden
                                className="translate-x-[3px]"
                            >
                                <path d="M8 5v14l11-7z" />
                            </svg>
                        </button>
                    </div>
                </Reveal>
            </div>

            <Lightbox item={open} onClose={() => setOpen(null)} />
        </section>
    );
}

export function Objectives({ objectives }: { objectives: FocusObjective[] }) {
    const [open, setOpen] = useState(0);

    return (
        <section className="bg-white py-16 lg:py-24">
            <div className="mx-auto max-w-[1640px] px-5 lg:px-[140px]">
                <div className="rounded-[20px] bg-[#f4f4f4] px-6 py-10 lg:px-16 lg:py-16">
                    <h2 className="font-primary text-[32px] font-bold tracking-[-1px] text-[#08080c] lg:text-[40px]">
                        Objectives
                    </h2>

                    <div className="mt-10 flex flex-col gap-10 lg:flex-row lg:items-start lg:gap-20">
                        <div className="lg:flex-1">
                            {objectives.map((o, i) => {
                                const active = i === open;
                                return (
                                    <button
                                        key={o.title}
                                        type="button"
                                        onClick={() => setOpen(i)}
                                        aria-expanded={active}
                                        className="flex w-full gap-6 border-b border-[#dedede] py-6 text-left last:border-0"
                                    >
                                        <span
                                            className={`shrink-0 text-[13px] font-semibold ${active ? 'text-[#08080c]' : 'text-[#b6b6b6]'}`}
                                        >
                                            {String(i + 1).padStart(2, '0')}
                                        </span>
                                        <span className="min-w-0 flex-1">
                                            <span
                                                className={`block text-[19px] font-bold leading-[1.35] lg:text-[21px] ${active ? 'text-[#08080c]' : 'text-[#b6b6b6]'}`}
                                            >
                                                {o.title}
                                            </span>
                                            <span
                                                className={`grid transition-[grid-template-rows,opacity] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                                                    active
                                                        ? 'mt-3 grid-rows-[1fr] opacity-100'
                                                        : 'grid-rows-[0fr] opacity-0'
                                                }`}
                                            >
                                                <span className="overflow-hidden">
                                                    <span className="block max-w-[520px] text-[13px] leading-[1.8] text-[#5e5e5e]">
                                                        {o.body}
                                                    </span>
                                                </span>
                                            </span>
                                        </span>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="lg:w-[460px] lg:shrink-0">
                            <Img src={OBJECTIVE_SHOT} alt="" className="w-full object-contain" />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ── Content filed under this focus area ──────────────────────────────── */

/** Section head shared by the stories, gallery and document bands. */
function BandHead({
    label,
    lead,
    bold,
    intro,
    action,
}: {
    label: string;
    lead: string;
    bold: string;
    intro: string;
    action: { to: string; text: string };
}) {
    return (
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
            <div>
                <SectionLabel>{label}</SectionLabel>
                <h2 className="font-primary mt-4 text-[32px] font-bold leading-[1.15] tracking-[-1.2px] lg:text-[42px]">
                    <span className="text-[#b3b3b3]">{lead} </span>
                    <span className="text-[#08080c]">{bold}</span>
                </h2>
                <p className="mt-4 max-w-[560px] text-[13px] leading-[1.75] text-[#3d3d3d]">{intro}</p>
            </div>

            <Link
                to={action.to}
                className="inline-flex shrink-0 items-center justify-center self-start rounded-[8px] bg-[#f60] px-6 py-3 text-[14px] font-bold text-white transition-transform duration-300 hover:scale-[1.03] sm:self-auto"
            >
                {action.text}
            </Link>
        </div>
    );
}

/** Stories filed under this programme. */
export function FocusStories({ area, content }: { area: string; content: FocusContent }) {
    const stories = focusStories(content);
    if (!stories.length) return null;

    return (
        <section id="stories" className="bg-[#f4f4f4] py-16 lg:py-24">
            <div className="mx-auto max-w-[1640px] px-5 lg:px-[140px]">
                <BandHead
                    label="Stories"
                    lead="The people behind"
                    bold={area}
                    intro="What this work looks like from the other side of it, written up from the case files."
                    action={{ to: '/resources/stories', text: 'View All Stories' }}
                />
                <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    {stories.map((st) => (
                        <ArticleCard key={st.slug} item={st} basePath="/resources/stories" />
                    ))}
                </div>
            </div>
        </section>
    );
}

/** Articles filed under this programme. */
export function FocusArticles({ area, content }: { area: string; content: FocusContent }) {
    const articles = focusArticles(content);
    if (!articles.length) return null;

    return (
        <section id="articles" className="bg-white py-16 lg:py-24">
            <div className="mx-auto max-w-[1640px] px-5 lg:px-[140px]">
                <BandHead
                    label="Articles"
                    lead="Analysis from"
                    bold={area}
                    intro="Field notes, findings and argument from the people doing the work."
                    action={{ to: '/resources/blog', text: 'View All Articles' }}
                />
                <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    {articles.map((a) => (
                        <ArticleCard key={a.slug} item={a} />
                    ))}
                </div>
            </div>
        </section>
    );
}

/** Films and photographs from this programme. */
export function FocusGallery({ area, content }: { area: string; content: FocusContent }) {
    const items = focusGallery(content);
    if (!items.length) return null;

    return (
        <section id="gallery" className="bg-[#f4f4f4] py-16 lg:py-24">
            <div className="mx-auto max-w-[1640px] px-5 lg:px-[140px]">
                <BandHead
                    label="Gallery"
                    lead="Inside"
                    bold={area}
                    intro="Clinics, launches and training weeks — the work as it actually looks."
                    action={{ to: '/resources/gallery', text: 'View Full Gallery' }}
                />
                <GalleryGrid items={items} />
            </div>
        </section>
    );
}

const DOC_TABS: { id: DocCollection; label: string; basePath: string }[] = [
    { id: 'publications', label: 'Publications', basePath: '/resources/publications' },
    { id: 'reports', label: 'Reports', basePath: '/resources/reports' },
    { id: 'infographics', label: 'Infographics', basePath: '/resources/infographics' },
];

/**
 * Publications, reports and infographics filed under this programme. They share
 * one card, so a single tabbed band covers all three. The band is only shown
 * when something is filed under at least one tab.
 */
export function FocusDocuments({ area, content }: { area: string; content: FocusContent }) {
    const [tab, setTab] = useState(DOC_TABS[0]);
    const docs = focusDocuments(content);
    if (DOC_TABS.every((t) => docs[t.id].length === 0)) return null;

    const shown = docs[tab.id];

    return (
        <section id="documents" className="bg-white py-16 lg:py-24">
            <div className="mx-auto max-w-[1640px] px-5 lg:px-[140px]">
                <BandHead
                    label="Documents"
                    lead="The evidence behind"
                    bold={area}
                    intro="Research, monitoring findings and share-ready visuals — free to read and download."
                    action={{ to: tab.basePath, text: `View All ${tab.label}` }}
                />

                <div role="tablist" aria-label="Documents by kind" className="mt-8 flex flex-wrap gap-2">
                    {DOC_TABS.map((t) => {
                        const active = tab.id === t.id;
                        return (
                            <button
                                key={t.id}
                                type="button"
                                role="tab"
                                aria-selected={active}
                                onClick={() => setTab(t)}
                                className={`rounded-full px-5 py-2.5 text-[13px] font-bold transition-colors ${
                                    active
                                        ? 'bg-[#08080c] text-white'
                                        : 'border border-[#e2e2e2] bg-white text-[#5e5e5e] hover:border-[#08080c] hover:text-[#08080c]'
                                }`}
                            >
                                {t.label}
                            </button>
                        );
                    })}
                </div>

                {shown.length ? (
                    <DownloadGrid items={shown} basePath={tab.basePath} />
                ) : (
                    <p className="mt-8 rounded-[10px] border border-dashed border-[#d0d0d0] px-6 py-10 text-center text-[13px] text-[#5e5e5e]">
                        No {tab.label.toLowerCase()} filed under this programme yet.
                    </p>
                )}
            </div>
        </section>
    );
}

/* ── Closing statement ────────────────────────────────────────────────── */

/**
 * The closing statement. Two-tone — a dark opening and a grey remainder — and
 * per-area, so each Focus Area page can make its own case.
 */
export function StatementBlock({ dark, grey }: { dark: string; grey: string }) {
    return (
        <section className="bg-white pb-16 lg:pb-24">
            <div className="mx-auto max-w-[1640px] px-5 lg:px-[140px]">
                <p className="font-primary text-[28px] font-medium leading-[1.4] tracking-[-0.8px] lg:text-[44px]">
                    <span className="text-[#08080c]">{dark} </span>
                    <span className="text-[#b6b6b6]">{grey}</span>
                </p>
            </div>
        </section>
    );
}
